import React, { useEffect, useState, useRef } from 'react';
import { alertsAPI } from '../../lib/api';
import { ALL_SYMBOLS, subscribeMultiTicker, fmtPrice } from '../../lib/market';
import { useToast } from '../../hooks/use-toast';
import { Bell, BellRing, Trash2, Plus, Check, X as XIcon, TrendingUp, TrendingDown, Volume2 } from 'lucide-react';

const beep = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.5);
  } catch (e) {}
};

const Alerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [prices, setPrices] = useState({});
  const [selSymbol, setSelSymbol] = useState(ALL_SYMBOLS[0].symbol);
  const [condition, setCondition] = useState('above');
  const [priceInput, setPriceInput] = useState('');
  const [notifPerm, setNotifPerm] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'default');
  const firedRef = useRef(new Set());
  const { toast } = useToast();

  const loadAlerts = async () => {
    try { setAlerts(await alertsAPI.list()); } catch {}
  };
  useEffect(() => { loadAlerts(); }, []);

  // Live price stream for all symbols with active alerts + selected symbol for the form
  useEffect(() => {
    const targetSyms = new Set(alerts.map(a => a.symbol));
    targetSyms.add(selSymbol);
    const symObjs = ALL_SYMBOLS.filter(s => targetSyms.has(s.symbol));
    if (symObjs.length === 0) return;
    const ws = subscribeMultiTicker(symObjs, (t) => {
      setPrices(prev => ({ ...prev, [t.symbol]: t }));
    });
    return () => { try { ws.close(); } catch {} };
  }, [alerts, selSymbol]);

  // Trigger checks
  useEffect(() => {
    for (const a of alerts) {
      if (a.triggered) continue;
      const p = prices[a.symbol]?.last;
      if (!p) continue;
      const hit = a.condition === 'above' ? p >= a.price : p <= a.price;
      if (hit && !firedRef.current.has(a.id)) {
        firedRef.current.add(a.id);
        const msg = `${a.display || a.symbol} is ${a.condition} $${a.price} (now $${p.toFixed(4)})`;
        toast({ title: 'Price alert triggered', description: msg });
        beep();
        if (notifPerm === 'granted') {
          try { new Notification('NEXUS FX PRO Price Alert', { body: msg, icon: '/favicon.ico' }); } catch {}
        }
        alertsAPI.trigger(a.id).then(loadAlerts).catch(() => {});
      }
    }
  }, [prices, alerts, notifPerm, toast]);

  const askNotif = async () => {
    if (typeof Notification === 'undefined') return;
    const p = await Notification.requestPermission();
    setNotifPerm(p);
    if (p === 'granted') toast({ title: 'Notifications enabled', description: 'You will be alerted at the OS level.' });
  };

  const create = async (e) => {
    e.preventDefault();
    const price = parseFloat(priceInput);
    if (!price || price <= 0) { toast({ title: 'Invalid price' }); return; }
    const sym = ALL_SYMBOLS.find(s => s.symbol === selSymbol);
    try {
      const alert = await alertsAPI.create({
        symbol: sym.symbol,
        display: sym.display,
        condition,
        price,
        provider: sym.provider
      });
      setAlerts(prev => [alert, ...prev]);
      setPriceInput('');
      toast({ title: 'Alert created', description: `Notify when ${sym.display} is ${condition} $${price}` });
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.detail || 'Try again' });
    }
  };

  const remove = async (id) => {
    try {
      await alertsAPI.delete(id);
      firedRef.current.delete(id);
      loadAlerts();
    } catch {}
  };

  const currentPriceForSel = prices[selSymbol]?.last;
  const selObj = ALL_SYMBOLS.find(s => s.symbol === selSymbol);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <BellRing size={22} className="text-[#00e676]"/> Price Alerts
          </h1>
          <p className="text-sm text-gray-400">Get notified the moment a market hits your target price. Toast + sound + browser notifications.</p>
        </div>
        {notifPerm !== 'granted' && (
          <button onClick={askNotif} className="btn-outline-green px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2">
            <Bell size={14}/> Enable Browser Notifications
          </button>
        )}
        {notifPerm === 'granted' && (
          <div className="flex items-center gap-2 text-xs text-[#00e676]">
            <Check size={14}/> Browser notifications enabled
          </div>
        )}
      </div>

      {/* Create form */}
      <div className="p-5 rounded-xl border border-[#1a2823] bg-[#0a1210]">
        <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2"><Plus size={14}/> Create New Alert</h3>
        <form onSubmit={create} className="grid md:grid-cols-4 gap-3">
          <div>
            <label className="text-[10px] text-gray-400 uppercase tracking-wider">Symbol</label>
            <select value={selSymbol} onChange={(e) => setSelSymbol(e.target.value)} className="mt-1 w-full bg-[#050807] border border-[#1a2823] rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00e676]">
              {ALL_SYMBOLS.map(s => <option key={s.symbol} value={s.symbol}>{s.display} ({s.class})</option>)}
            </select>
            {currentPriceForSel && <div className="mt-1 text-[11px] mono text-gray-400">Current: <span className="text-white">{fmtPrice(currentPriceForSel, selObj?.pricePrecision || 2)}</span></div>}
          </div>
          <div>
            <label className="text-[10px] text-gray-400 uppercase tracking-wider">Condition</label>
            <div className="flex mt-1 bg-[#050807] rounded border border-[#1a2823] p-0.5">
              <button type="button" onClick={() => setCondition('above')} className={`flex-1 py-1.5 text-xs font-semibold rounded flex items-center justify-center gap-1 ${condition === 'above' ? 'bg-[#00e676] text-black' : 'text-gray-400'}`}>
                <TrendingUp size={12}/> Above
              </button>
              <button type="button" onClick={() => setCondition('below')} className={`flex-1 py-1.5 text-xs font-semibold rounded flex items-center justify-center gap-1 ${condition === 'below' ? 'bg-red-500 text-white' : 'text-gray-400'}`}>
                <TrendingDown size={12}/> Below
              </button>
            </div>
          </div>
          <div>
            <label className="text-[10px] text-gray-400 uppercase tracking-wider">Target Price</label>
            <input type="number" step="any" value={priceInput} onChange={(e) => setPriceInput(e.target.value)} placeholder={currentPriceForSel ? currentPriceForSel.toFixed(selObj?.pricePrecision || 2) : '0.00'} className="mt-1 w-full bg-[#050807] border border-[#1a2823] rounded px-3 py-2 text-sm text-white mono focus:outline-none focus:border-[#00e676]"/>
          </div>
          <div className="flex items-end">
            <button type="submit" className="btn-green w-full py-2 rounded font-semibold text-sm">Create Alert</button>
          </div>
        </form>
      </div>

      {/* Active + Triggered */}
      <div className="rounded-xl border border-[#1a2823] bg-[#0a1210] overflow-hidden">
        <div className="px-4 py-3 border-b border-[#141f1b] flex items-center justify-between">
          <h3 className="text-sm font-bold text-white">Your Alerts ({alerts.length})</h3>
          <div className="text-[10px] text-gray-400 flex items-center gap-1"><Volume2 size={11}/> Live monitoring</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] text-gray-500 uppercase tracking-wider">
                <th className="px-4 py-2">Symbol</th>
                <th>Condition</th>
                <th>Target</th>
                <th>Current</th>
                <th>Distance</th>
                <th>Status</th>
                <th className="text-right pr-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {alerts.length === 0 && <tr><td colSpan="7" className="py-10 text-center text-sm text-gray-500">No alerts yet. Create one above to get notified when a price hits your target.</td></tr>}
              {alerts.map(a => {
                const cur = prices[a.symbol]?.last;
                const diff = cur ? cur - a.price : null;
                const diffPct = cur ? ((cur - a.price) / a.price) * 100 : null;
                const isCondMet = cur ? (a.condition === 'above' ? cur >= a.price : cur <= a.price) : false;
                return (
                  <tr key={a.id} className={`border-t border-[#141f1b] ${a.triggered ? 'opacity-60' : ''}`}>
                    <td className="px-4 py-3 text-white font-semibold">{a.display || a.symbol}</td>
                    <td>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${a.condition === 'above' ? 'bg-[#00e676]/15 text-[#00e676]' : 'bg-red-500/15 text-red-400'}`}>
                        {a.condition}
                      </span>
                    </td>
                    <td className="mono text-white">${a.price}</td>
                    <td className="mono text-gray-300">{cur ? `$${cur.toFixed(4)}` : '—'}</td>
                    <td className={`mono text-xs ${diffPct && Math.abs(diffPct) < 1 ? 'text-amber-400' : 'text-gray-400'}`}>
                      {diffPct !== null ? `${diffPct >= 0 ? '+' : ''}${diffPct.toFixed(2)}%` : '—'}
                    </td>
                    <td>
                      {a.triggered ? (
                        <span className="text-[10px] text-gray-400 flex items-center gap-1"><Check size={10}/> Triggered</span>
                      ) : isCondMet ? (
                        <span className="text-[10px] text-[#00e676] font-bold pulse-dot inline-flex items-center gap-1">● FIRING</span>
                      ) : (
                        <span className="text-[10px] text-gray-500 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#00e676] pulse-dot"/> Watching</span>
                      )}
                    </td>
                    <td className="text-right pr-4">
                      <button onClick={() => remove(a.id)} className="p-1.5 rounded border border-[#1a2823] text-gray-400 hover:text-red-400 hover:border-red-500/40 transition">
                        <Trash2 size={12}/>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-gray-500">
        Alerts monitor live streaming prices. When a target is hit you'll see a toast, hear a soft chime, and get a browser notification (if enabled). Alerts persist in your account.
      </p>
    </div>
  );
};

export default Alerts;
